import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Snackbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  InputAdornment,
  IconButton,
  Switch,
  FormControlLabel,
  CircularProgress,
} from '@mui/material';
import {
  Visibility,
  VisibilityOff,
  Save as SaveIcon,
  Cancel as CancelIcon,
  PersonAdd as PersonAddIcon,
} from '@mui/icons-material';
import { userService, CreateUserData, UpdateUserData } from '../../services/userService';
import { User } from '../../contexts/AuthContext';

interface SimpleUserFormProps {
  user?: User | null;
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

interface FormData {
  username: string;
  email: string;
  password: string;
  confirmPassword: string;
  first_name: string;
  last_name: string;
  role: 'admin' | 'veterinaire' | 'proprietaire';
  telephone: string;
  is_active: boolean;
}

interface FormErrors {
  username?: string;
  email?: string;
  password?: string;
  confirmPassword?: string;
  first_name?: string;
  last_name?: string;
  general?: string;
}

export const SimpleUserForm: React.FC<SimpleUserFormProps> = ({ user, open, onClose, onSuccess }) => {
  const isEditing = !!user;
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  
  const [formData, setFormData] = useState<FormData>({
    username: '',
    email: '',
    password: '',
    confirmPassword: '',
    first_name: '',
    last_name: '',
    role: 'proprietaire',
    telephone: '',
    is_active: true,
  });

  const [errors, setErrors] = useState<FormErrors>({});
  
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error' | 'warning' | 'info';
  }>({
    open: false,
    message: '',
    severity: 'success',
  });

  // Pré-remplir le formulaire en mode édition
  useEffect(() => {
    if (user && open) {
      setFormData({
        username: user.username,
        email: user.email,
        password: '',
        confirmPassword: '',
        first_name: user.first_name,
        last_name: user.last_name,
        role: user.role,
        telephone: user.telephone || '',
        is_active: user.is_active,
      });
      setErrors({});
    } else if (!user && open) {
      setFormData({
        username: '',
        email: '',
        password: '',
        confirmPassword: '',
        first_name: '',
        last_name: '',
        role: 'proprietaire',
        telephone: '',
        is_active: true,
      });
      setErrors({});
    }
  }, [user, open]);

  // Afficher une notification
  const showSnackbar = (message: string, severity: 'success' | 'error' | 'warning' | 'info') => {
    setSnackbar({ open: true, message, severity });
  };

  // Gérer les changements de champ
  const handleFieldChange = (field: keyof FormData) => (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | any
  ) => {
    const value = event.target.type === 'checkbox' ? event.target.checked : event.target.value;
    setFormData(prev => ({ ...prev, [field]: value }));
    
    // Effacer l'erreur du champ modifié
    if (errors[field as keyof FormErrors]) {
      setErrors(prev => ({ ...prev, [field]: undefined }));
    }
  };

  // Valider le formulaire
  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    // Validation du nom d'utilisateur
    if (!formData.username.trim()) {
      newErrors.username = 'Le nom d\'utilisateur est requis';
    } else if (formData.username.length < 3) {
      newErrors.username = 'Le nom d\'utilisateur doit contenir au moins 3 caractères';
    }

    // Validation de l'email
    if (!formData.email.trim()) {
      newErrors.email = 'L\'email est requis';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Format d\'email invalide';
    }

    // Validation du mot de passe (requis uniquement en création)
    if (!isEditing) {
      if (!formData.password) {
        newErrors.password = 'Le mot de passe est requis';
      } else if (formData.password.length < 6) {
        newErrors.password = 'Le mot de passe doit contenir au moins 6 caractères';
      }

      if (!formData.confirmPassword) {
        newErrors.confirmPassword = 'La confirmation du mot de passe est requise';
      } else if (formData.password !== formData.confirmPassword) {
        newErrors.confirmPassword = 'Les mots de passe ne correspondent pas';
      }
    } else if (formData.password && formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Les mots de passe ne correspondent pas';
    }

    // Validation du prénom et nom
    if (!formData.first_name.trim()) {
      newErrors.first_name = 'Le prénom est requis';
    }
    if (!formData.last_name.trim()) {
      newErrors.last_name = 'Le nom est requis';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Soumettre le formulaire
  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    
    try {
      if (isEditing && user) {
        // Modification d'utilisateur
        const updateData: UpdateUserData = {
          email: formData.email,
          first_name: formData.first_name,
          last_name: formData.last_name,
          telephone: formData.telephone || undefined,
          is_active: formData.is_active,
        };

        // Inclure le mot de passe s'il est fourni
        if (formData.password) {
          (updateData as any).password = formData.password;
        }

        await userService.update(user.id, updateData);
        
        showSnackbar('Utilisateur modifié avec succès', 'success');
      } else {
        // Création d'utilisateur
        const createData: CreateUserData = {
          username: formData.username,
          email: formData.email,
          password: formData.password,
          first_name: formData.first_name,
          last_name: formData.last_name,
          role: formData.role,
          telephone: formData.telephone || undefined,
        };

        await userService.create(createData);
        showSnackbar('Utilisateur créé avec succès', 'success');
      }

      // Fermer le formulaire et rafraîchir la liste
      setTimeout(() => {
        onSuccess();
        onClose();
      }, 1000);
      
    } catch (error: any) {
      console.error('Erreur lors de la sauvegarde:', error);
      
      if (error.response?.data) {
        const apiErrors = error.response.data;
        const newErrors: FormErrors = {};
        
        // Mapper les erreurs de l'API vers le formulaire
        Object.keys(apiErrors).forEach(key => {
          if (key in formData) {
            newErrors[key as keyof FormErrors] = Array.isArray(apiErrors[key]) 
              ? apiErrors[key][0] 
              : apiErrors[key];
          }
        });
        
        if (Object.keys(newErrors).length > 0) {
          setErrors(newErrors);
        } else {
          setErrors({ general: 'Erreur lors de la sauvegarde' });
        }
      } else {
        setErrors({ general: 'Erreur lors de la sauvegarde' });
      }
    } finally {
      setLoading(false);
    }
  };

  // Gérer la fermeture
  const handleClose = () => {
    if (!loading) {
      onClose();
    }
  };

  return (
    <>
      <Dialog 
        open={open} 
        onClose={handleClose} 
        maxWidth="sm" 
        fullWidth
        PaperProps={{
          component: 'form',
          onSubmit: handleSubmit,
        }}
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <PersonAddIcon />
            <Typography variant="h6">
              {isEditing ? `Modifier ${user?.username}` : 'Créer un utilisateur'}
            </Typography>
          </Box>
        </DialogTitle>

        <DialogContent dividers>
          {errors.general && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {errors.general}
            </Alert>
          )}

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {/* Nom d'utilisateur */}
            <TextField
              fullWidth
              label="Nom d'utilisateur"
              value={formData.username}
              onChange={handleFieldChange('username')}
              error={!!errors.username}
              helperText={errors.username}
              disabled={isEditing}
              required
            />
            
            {/* Email */}
            <TextField
              fullWidth
              type="email"
              label="Email"
              value={formData.email}
              onChange={handleFieldChange('email')}
              error={!!errors.email}
              helperText={errors.email}
              required
            />

            {/* Mot de passe */}
            <TextField
              fullWidth
              type={showPassword ? 'text' : 'password'}
              label={isEditing ? 'Nouveau mot de passe (optionnel)' : 'Mot de passe'}
              value={formData.password}
              onChange={handleFieldChange('password')}
              error={!!errors.password}
              helperText={errors.password}
              required={!isEditing}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            {/* Confirmation mot de passe */}
            <TextField
              fullWidth
              type={showConfirmPassword ? 'text' : 'password'}
              label="Confirmer le mot de passe"
              value={formData.confirmPassword}
              onChange={handleFieldChange('confirmPassword')}
              error={!!errors.confirmPassword}
              helperText={errors.confirmPassword}
              required={!isEditing}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      edge="end"
                    >
                      {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            {/* Prénom */}
            <TextField
              fullWidth
              label="Prénom"
              value={formData.first_name}
              onChange={handleFieldChange('first_name')}
              error={!!errors.first_name}
              helperText={errors.first_name}
              required
            />

            {/* Nom */}
            <TextField
              fullWidth
              label="Nom"
              value={formData.last_name}
              onChange={handleFieldChange('last_name')}
              error={!!errors.last_name}
              helperText={errors.last_name}
              required
            />

            {/* Rôle */}
            <FormControl fullWidth required={!isEditing}>
              <InputLabel>Rôle</InputLabel>
              <Select
                value={formData.role}
                label="Rôle"
                onChange={handleFieldChange('role')}
                disabled={isEditing}
              >
                <MenuItem value="proprietaire">Propriétaire</MenuItem>
                <MenuItem value="veterinaire">Vétérinaire</MenuItem>
                <MenuItem value="admin">Administrateur</MenuItem>
              </Select>
            </FormControl>

            {/* Téléphone */}
            <TextField
              fullWidth
              label="Téléphone (optionnel)"
              value={formData.telephone}
              onChange={handleFieldChange('telephone')}
            />

            {/* Statut (uniquement en édition) */}
            {isEditing && (
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.is_active}
                    onChange={handleFieldChange('is_active')}
                  />
                }
                label="Compte actif"
              />
            )}
          </Box>
        </DialogContent>

        <DialogActions sx={{ p: 2 }}>
          <Button 
            onClick={handleClose}
            disabled={loading}
            startIcon={<CancelIcon />}
          >
            Annuler
          </Button>
          <Button 
            type="submit"
            variant="contained"
            disabled={loading}
            startIcon={loading ? <CircularProgress size={16} /> : <SaveIcon />}
          >
            {loading ? 'Sauvegarde...' : (isEditing ? 'Modifier' : 'Créer')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar pour les notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert 
          onClose={() => setSnackbar({ ...snackbar, open: false })} 
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
}; 